import { Link } from "react-router-dom";

const features = [
  { icon: "ri-flashlight-line", text: "Instant shot allowance calculations" },
  { icon: "ri-team-line", text: "Singles, fourball, foursomes, greensomes & more" },
  { icon: "ri-flag-line", text: "Works with rated golf courses worldwide" },
  { icon: "ri-trophy-line", text: "Ideal for clubs, societies & away days" },
];

const useCases = [
  { icon: "ri-shield-star-line", title: "Competition secretaries", body: "Calculate playing handicaps before every tee sheet — no spreadsheet errors." },
  { icon: "ri-suitcase-line", title: "Ryder Cup–style tours", body: "Coordinate strokes across teams for weekend trips and groups." },
  { icon: "ri-group-line", title: "Fourball partners", body: "Work out better-ball strokes in seconds before you tee off." },
  { icon: "ri-map-pin-line", title: "Visiting golfers", body: "Confirm your match play shots at any new course in the world." },
];

export default function WhatItDoes() {
  return (
    <section id="explore" className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.18em] text-primary-700 font-semibold">What it does</span>
          <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl lg:text-5xl leading-tight">
            <a href="#what-it-does">Built around the World Handicap System™</a>
          </h2>
          <p className="mt-4 text-foreground-700 text-base md:text-lg leading-relaxed">
            HANDICALC® applies the WHS™ to your handicap and course details to calculate your correct
            shot allowances. Supporting 20+ formats — from singles and fourball to scrambles, betterball and team events — it works with any course that has a rating, handles mixed tees automatically, and delivers results in under a second.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Big feature card */}
          <article className="lg:col-span-7 rounded-2xl bg-background-100 border border-background-200 p-7 md:p-10">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-600 text-background-50">
                <i className="ri-calculator-line text-xl"></i>
              </span>
              <h3 className="font-heading font-semibold text-foreground-950 text-xl md:text-2xl">
                <a href="#features">Calculate strokes the right way, every time</a>
              </h3>
            </div>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <li key={f.text} className="flex items-start gap-3 rounded-lg bg-background-50 border border-background-200 px-4 py-3">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                    <i className={`${f.icon} text-sm`}></i>
                  </span>
                  <span className="text-sm text-foreground-800 leading-snug">{f.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7">
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-600 hover:bg-primary-700 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                Open HANDICALC® <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          {/* Use cases */}
          <aside className="lg:col-span-5 rounded-2xl bg-accent-50 border border-accent-200 p-7 md:p-10">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                <i className="ri-bookmark-line text-xl"></i>
              </span>
              <h3 className="font-heading font-semibold text-foreground-950 text-xl md:text-2xl">
                <a href="#use-cases">Example use cases</a>
              </h3>
            </div>

            <ul className="mt-6 space-y-4">
              {useCases.map((u) => (
                <li key={u.title} className="flex gap-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-accent-700 border border-accent-200 shrink-0">
                    <i className={`${u.icon}`}></i>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{u.title}</div>
                    <p className="mt-0.5 text-sm text-foreground-700 leading-snug">{u.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-xs text-foreground-600 italic">
              Always verify allowances against your club's competition rules.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}